import { useState, useEffect, useCallback } from "react";
import { Label, Input } from "@tellescope/design-system";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@tellescope/ui";
import { useDebouncedValue } from "@tellescope/react-hooks";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useAuth } from "@tellescope/tanstack-query";
import { useEnduserInitFromToken } from "../../enduser/store/hooks";
import { invalidateEnduserQueries } from "../../enduser/components/enduserQueryClient";
import type { Enduser } from "@tellescope/types-models";

interface EnduserSelectorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// TODO: investigate moving the enduser search to PersonSelect (@tellescope/person-select). Not moved yet because this
//   is an impersonation dialog with a clickable result list, not a form control.
export const EnduserSelector: React.FC<EnduserSelectorProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const { session } = useAuth();
  const initFromToken = useEnduserInitFromToken();

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, 300);
  const [endusers, setEndusers] = useState<Enduser[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [selectedEnduserId, setSelectedEnduserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const performSearch = useCallback(
    async (query: string) => {
      if (!session || !query.trim()) {
        setEndusers([]);
        setHasSearched(false);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const results = await session.api.endusers.getSome({
          search: { query },
          limit: 10,
        });
        setEndusers(results);
        setHasSearched(true);
      } catch (err) {
        setError(t("Failed to search endusers"));
        console.error("Search error:", err);
      } finally {
        setIsSearching(false);
      }
    },
    [session, t]
  );

  // Trigger search when debounced query changes
  useEffect(() => {
    if (!debouncedSearchQuery.trim()) {
      setEndusers([]);
      setHasSearched(false);
      return;
    }

    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery, performSearch]);

  // Clear state when dialog closes
  useEffect(() => {
    if (!open) {
      setSearchQuery("");
      setEndusers([]);
      setHasSearched(false);
      setError(null);
    }
  }, [open]);

  const handleSelectEnduser = async (enduser: Enduser) => {
    if (!session) return;

    setIsAuthenticating(true);
    setSelectedEnduserId(enduser.id);
    setError(null);

    try {
      // Generate auth token for the selected enduser
      const tokenResult = await session.api.endusers.generate_auth_token({
        id: enduser.id,
        overrideConsent: true,
        overrideOTP: true,
      });

      // Initialize enduser session with the generated token
      const success = await initFromToken(tokenResult.authToken, session.userInfo.businessId);

      if (success) {
        // Invalidate all enduser queries to refresh data for the new enduser
        await invalidateEnduserQueries();
        onOpenChange(false);
        // State cleanup handled by dialog close effect
      } else {
        setError(t("Failed to authenticate as enduser"));
      }
    } catch (err) {
      setError(t("Failed to generate authentication token"));
      console.error("Auth error:", err);
    } finally {
      setIsAuthenticating(false);
      setSelectedEnduserId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("Select Enduser")}</DialogTitle>
          <DialogDescription>{t("Search for an enduser to preview the portal as them.")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="space-y-2">
            <Label htmlFor="search">{t("Search by name, email, or phone")}</Label>
            <div className="relative">
              <Input
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("Start typing to search...")}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>

          {/* Error message */}
          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Results list */}
          {endusers.length > 0 && (
            <div className="space-y-2">
              <Label>{t("Results")}</Label>
              <div className="border rounded-md max-h-64 overflow-auto">
                {endusers.map((enduser) => (
                  <button
                    key={enduser.id}
                    onClick={() => handleSelectEnduser(enduser)}
                    disabled={isAuthenticating}
                    className="w-full px-4 py-3 text-left hover:bg-muted border-b last:border-b-0 flex items-center justify-between disabled:opacity-50"
                  >
                    <div>
                      <div className="font-medium">
                        {enduser.fname} {enduser.lname}
                      </div>
                      {enduser.email && <div className="text-sm text-muted-foreground">{enduser.email}</div>}
                      {enduser.phone && <div className="text-sm text-muted-foreground">{enduser.phone}</div>}
                    </div>
                    {isAuthenticating && selectedEnduserId === enduser.id && (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* No results message */}
          {!isSearching && hasSearched && endusers.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              {t("No endusers found matching your search.")}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
